import { h } from 'preact';
import Store from 'store/store';
import { getYoutubeId, isYoutubePlaylist } from 'utils/assets';
import { getAssetThumb } from 'utils/assetThumbnail';
import * as jsonPointer from 'utils/jsonPointer';
import { mergeInto } from 'utils/merge';
import { confirm } from 'utils/modal';
import { canUser } from 'utils/roles';
import { isBaseRegion } from 'utils/studioConfig';
import { joinClasses } from 'utils/utils';
import { fetchVideoTitle } from 'utils/videoTitle';

import Plus from 'components/icons/plus';
import Button from 'components/button/button';
import EditorContent from 'components/editor/content';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import MediaField from 'components/editor/settings/mediaField';
import InheritIndicator from 'components/editor/settings/inheritIndicator';

import s from 'components/editor/movies/movieMedia/mediaAsset.sss';

/**
 * This component is used to render a single media asset (image or video) in the movie media editor. It is used for both the hero image and the trailers, as well as any other media assets that may be added in the future.
 * Props:
 * - `path`: The path to the asset in the data store.
 * - `label`: The label to display under the asset thumbnail.
 * - `extraClass`: An optional extra class to apply to the asset container for additional styling.
 * - `isVideo`: Whether the asset is a video or not. This is used to determine how to render the thumbnail.
 * - `dataRoot`: The root path in the data store where the asset data is located. This is used to read the latest data for the asset.
 */
export default class MediaAsset extends EditorContent {

	get displayedValue() {
		const { dataRoot, inheritPath, path } = this.props;
		const value = jsonPointer.get(Store.get().data, `${dataRoot}${path}`);
		const inheritedValue = inheritPath && jsonPointer.get(Store.get().data, `${dataRoot}${inheritPath}`);
		return mergeInto(inheritedValue, value);
	}

	get altPlaceholder() {
		const { altPlaceholder } = this.props;
		const data = this.displayedValue;
		return typeof altPlaceholder === 'function' ? altPlaceholder(data) : altPlaceholder;
	}

	get label() {
		const { label } = this.props;
		const data = this.displayedValue;
		return typeof label === 'function' ? label(data) : label;
	}

	get canInherit() {
		const { dataRoot, inheritPath } = this.props;
		return inheritPath && !!jsonPointer.get(Store.get().data, `${dataRoot}${inheritPath}`);
	}

	constructor(props) {
		super(props);

		this.deleteAsset = this.deleteAsset.bind(this);
	}

	componentDidMount() {
		super.componentDidMount?.();

		// Add a default title for YouTube videos if we don't have one already
		const { dataRoot, path, youtube } = this.props;
		if (youtube) {
			const { defaultTitle, url } = jsonPointer.get(Store.get().data, `${dataRoot}${path}`) || {};
			if (!defaultTitle && url) this.getYTTitle(`${dataRoot}${path}`, url);
		}
	}

	// ========
	//  VISUAL
	// ========

	renderAssetThumbnailContent(url, isVideo) {
		if (!url) {
			return (
				<div class={s.text}>
					<Plus />
					<div>Add</div>
				</div>
			);
		}
		
		return getAssetThumb(url, isVideo, () => this.setState());
	}

	renderAsset(assetData) {
		const { anyType, dataRoot, isVideo, path } = this.props;
		
		const hasData = !!assetData?.url;
		const isPlaylist = isVideo && isYoutubePlaylist(assetData?.url);
		const assetType = anyType ? 'asset' : isVideo ? 'video' : 'image';

		return (
			<div class={joinClasses(s.thumbnail, hasData && s.edit, !hasData && s.add)}>
				{isPlaylist ? <div class={s.badge}>PLAYLIST</div> : null}
				{hasData && <Button class={s.button} primary>{`Edit ${assetType}`}</Button>}
				{this.canInherit && <InheritIndicator class={s.inheritIndicator} path={`${dataRoot}${path}/url`} align="middle" hideReset />}
				{this.renderAssetThumbnailContent(assetData?.url, isVideo)}
			</div>
		);
	}

	renderVisual() {
		const { helpTip, path, youtube } = this.props;
		const data = this.displayedValue;
		const assetLabel = (youtube ? (data?.title || data?.defaultTitle) : this.label) || null;

		return (
			<SelectableSection {...this.props} section={path} class={s.asset}>
				{this.renderAsset(data)}
				{assetLabel && <div class={s.label}>{assetLabel}</div>}
				{helpTip && <div class={s.help}>{helpTip}</div>}
			</SelectableSection>
		);
	}

	// ========
	// SETTINGS
	// ========

	async getYTTitle(path, url = null) {
		const videoData = await new Promise((resolve) => {
			// Listen for updates to the specific video data until we have a URL that matches the one provided (or any URL if none provided)
			const checkAndReturn = () => {
				const data = jsonPointer.get(Store.get().data, path);
				if (data && (!url || data.url === url)) resolve(data);
			};
			
			checkAndReturn();
			Store.on('update', checkAndReturn);
		});

		const src = videoData.url;
		if (!src) return;

		const newTitle = await fetchVideoTitle(src);
		if (newTitle !== videoData.defaultTitle) jsonPointer.set(Store.get().data, `${path}/defaultTitle`, newTitle);
	}

	async deleteAsset() {
		const ok = await confirm('Are you sure you want to delete this media asset?', { title: 'Delete media asset?', danger: true, button: 'Delete' });
		if (!ok) return;

		const { dataRoot, inheritPath, onDelete, path, select } = this.props;
		const isInherited = inheritPath && jsonPointer.get(Store.get().data, `${dataRoot}${inheritPath}`) !== undefined;

		// Soft delete so the removal propagates: to this locale when inherited, to every locale when editing the base movie
		if (isInherited || isBaseRegion()) {
			jsonPointer.setDeep(Store.get().data, `${dataRoot}${path}`, null);
		} else { // otherwise, remove the asset entirely
			jsonPointer.removeDeep(Store.get().data, `${dataRoot}${path}`);
		}

		select?.(null); // Deselect the asset if it was selected
		onDelete?.();
	}

	renderYoutubeSettings(data) {
		const readonly = !canUser('write');
		const { dataRoot, hideAlt, path } = this.props;

		data ??= jsonPointer.get(Store.get().data, `${dataRoot}${path}`);
		const src = data?.url;

		const youtubeId = getYoutubeId(src);
		const isPlaylist = isYoutubePlaylist(src);

		const thumbnailPreview = (
			<div>
				<div>Thumbnail preview</div>
				{(youtubeId || isPlaylist) ? getAssetThumb(src) : (
					<div class={s.noThumbnail}>
						<div>No thumbnail</div>
					</div>
				)}
			</div>
		);

		return [			
			<SimpleField
				dark
				label="Link"
				path={`${dataRoot}${path}/url`}
				disabled={readonly}
				onInput={(e) => {
					// Fetch title immediately when URL changes
					const url = e.target.value;
					// URL must be provided as Store will not have been updated yet
					if (url) this.getYTTitle(`${dataRoot}${path}`, url);
				}}
			/>,
			<SimpleField
				dark
				label="Title"
				path={`${dataRoot}${path}/title`}
				placeholder={data?.defaultTitle || 'Untitled Video'}
				disabled={readonly || isPlaylist}
			/>,
			!hideAlt && (
				<SimpleField
					dark
					label="Thumbnail image alt text"
					path={`${dataRoot}${path}/thumbnailAlt`}
					placeholder={data?.defaultTitle || 'Untitled Video'}
					disabled={readonly || isPlaylist}
				/>
			),
			thumbnailPreview
		].filter(Boolean);
	}

	renderMediaField(opts = {}) {
		const { assetData, config, dataRoot, helpTip, hideAlt, inheritPath, path } = this.props;

		return [
			<MediaField
				dark
				label="Asset"
				path={`${dataRoot}${path}/url`}
				inheritPath={`${dataRoot}${inheritPath}/url`}
				group={config?.id}
				assetData={assetData}
				helpTip={helpTip}
				{...opts}
			/>,
			!hideAlt && <SimpleField dark label="Alt text" path={`${dataRoot}${path}/alt`} placeholder={this.altPlaceholder} />
		].filter(Boolean);
	}

	renderVideoSettings() {
		return this.renderMediaField({
			label: 'Video',
			video: true,
		});
	}

	renderImageSettings() {
		return this.renderMediaField({
			label: 'Image',
			focalPointPath: this.props.focalPoint && `${this.props.dataRoot}${this.props.path}/focalPoint`,
		});
	}

	renderAssetSettings() {
		return this.renderMediaField({
			anyType: true,
		});
	}

	renderSettingsFields() {
		const { anyType, isVideo, youtube } = this.props;
		if (youtube) return this.renderYoutubeSettings();
		if (anyType) return this.renderAssetSettings();
		if (isVideo) return this.renderVideoSettings();
		return this.renderImageSettings();
	}

	renderSettings() {
		const { activeElement, canRemove, dataRoot, extraSettings, path } = this.props;
		if (!activeElement || activeElement !== path) return null;

		const readonly = !canUser('write');
		const hasData = !!jsonPointer.get(Store.get().data, `${dataRoot}${path}`) || this.canInherit;

		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation([{ label: this.label, path }])}
				<div class={s.settingsContent}>
					{this.renderSettingsFields()}
					{typeof extraSettings === 'function' ? extraSettings(this.props) : extraSettings}
					{canRemove && <Button danger icon="delete" onClick={this.deleteAsset} disabled={readonly || !hasData}>Delete</Button>}
				</div>
			</div>
		);
	}
}